<template>
  <div class="bg-black min-h-screen text-white">
    <div class="container mx-auto px-4 py-8">
      <!-- Settings Button -->
      <SettingsButton @toggle-settings="settingsManager.toggleSettings" />

      <!-- Settings Modal -->
      <SettingsModal 
        :show="settingsManager.showSettings.value"
        :settings="settingsManager.settings"
        @close="settingsManager.closeSettings"
        @save="handleSaveSettings"
        @add-member="settingsManager.addMember"
        @remove-member="settingsManager.removeMember"
      />

      <!-- Timer Display -->
      <div v-if="!showSummary" class="max-w-4xl mx-auto">
        <TimerDisplay 
          :current-phase="currentPhase"
          :remaining-time="timer.remainingTime.value"
          :time-expired="timer.timeExpired.value"
          :progress-percentage="progressPercentage"
          :phase-display-number="getPhaseDisplayNumber"
          :total-main-phases="totalMainPhases"
          :is-running="timer.isRunning.value"
          :can-go-previous="canGoPrevious"
          @toggle-timer="handleToggleTimer"
          @next-phase="handleNextPhase"
          @previous-phase="handlePreviousPhase"
          @reset-phase="handleResetPhase"
        />
      </div>
      
      <!-- Summary Screen -->
      <div v-else class="max-w-4xl mx-auto">
        <MeetingSummary 
          :phases="phases"
          :phase-elapsed-times="timer.phaseElapsedTimes.value"
          :total-elapsed-time="timer.totalElapsedTime.value"
          @restart-meeting="handleRestartMeeting"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useTimer } from '@/composables/useTimer.js';
import { useSettings } from '@/composables/useSettings.js';
import { PhaseService } from '@/services/PhaseService.js';
import TimerDisplay from '@/components/TimerDisplay.vue';
import SettingsModal from '@/components/SettingsModal.vue';
import MeetingSummary from '@/components/MeetingSummary.vue';
import SettingsButton from '@/components/SettingsButton.vue';

export default {
  name: 'App',
  components: {
    TimerDisplay,
    SettingsModal,
    MeetingSummary,
    SettingsButton
  },
  setup() {
    // Application state
    const showSummary = ref(false);
    
    // Composables
    const timer = useTimer();
    const settingsManager = useSettings();
    
    // Generated phases based on settings
    const phases = ref(PhaseService.generatePhases(settingsManager.settings));
    
    // Computed properties
    const currentPhase = computed(() => 
      PhaseService.getPhaseByIndex(phases.value, timer.currentPhaseIndex.value)
    );
    
    const progressPercentage = computed(() => 
      timer.getProgressPercentage(currentPhase.value)
    );
    
    const canGoPrevious = computed(() => 
      !PhaseService.isFirstPhase(timer.currentPhaseIndex.value)
    );
    
    // Phase display helpers
    const getPhaseDisplayNumber = computed(() => {
      const currentIndex = timer.currentPhaseIndex.value;
      const currentPhaseObj = currentPhase.value;
      
      if (!currentPhaseObj) return '1';
      
      if (currentPhaseObj.type === 'personal') {
        // Personal phases are sub-phases of phase 3
        const personalPhaseIndex = currentIndex - 2; // -2 because first 2 phases are deadline and projects
        return `3.${personalPhaseIndex + 1}`;
      } else if (currentPhaseObj.type === 'deadline') {
        return '1';
      } else if (currentPhaseObj.type === 'projects') {
        return '2';
      }
      
      return '1';
    });
    
    const totalMainPhases = computed(() => {
      return 3; // Always 3 main phases: Deadline, Projects, Personal Updates
    });
    
    // Initialize timer when phases change
    const initializePhases = () => {
      phases.value = PhaseService.generatePhases(settingsManager.settings);
      timer.initializeTimer(phases.value);
      showSummary.value = false;
    };
    
    // Watch for settings changes to regenerate phases
    watch(() => settingsManager.settings, initializePhases, { deep: true });
    
    // Initialize on mount
    timer.initializeTimer(phases.value);
    
    // Event handlers
    const handleToggleTimer = () => {
      timer.toggleTimer(currentPhase.value);
    };
    
    const handleNextPhase = () => {
      timer.nextPhase(phases.value, () => {
        showSummary.value = true;
      });
    };
    
    const handlePreviousPhase = () => {
      timer.previousPhase(phases.value);
    };
    
    const handleResetPhase = () => {
      timer.resetPhaseTimer(currentPhase.value);
    };
    
    const handleRestartMeeting = () => {
      initializePhases();
    };
    
    const handleSaveSettings = () => {
      const validation = settingsManager.validateSettings();
      if (validation.isValid) {
        settingsManager.saveSettings();
        // Force close the modal
        settingsManager.closeSettings();
      } else {
        // Show validation errors
        alert('Settings validation failed:\n' + validation.errors.join('\n'));
      }
    };
    
    return {
      // State
      showSummary,
      phases,
      
      // Composables
      timer,
      settingsManager,
      
      // Computed
      currentPhase,
      progressPercentage,
      canGoPrevious,
      getPhaseDisplayNumber,
      totalMainPhases,
      
      // Event handlers
      handleToggleTimer,
      handleNextPhase,
      handlePreviousPhase,
      handleResetPhase,
      handleRestartMeeting,
      handleSaveSettings
    };
  }
};
</script>